// Engineer-lane Tandem AI tools — delegate to TandemToolSystem file tools + tandemAPI.
// Mirrors workspace read-file / write-file / … names so models behave consistently.

import { TandemAIToolSet } from "../../base/TandemAIToolSet.js";
import { BaseAITool } from "../../../../base/BaseAITool.js";
import {
  captureProjectPathState,
  restoreProjectPathState,
} from "../../../shared/captureProjectPathState.js";
import { buildProjectFileTreeForOverview } from "../../../workspace/utils/projectOverviewFileTree.js";

const ENGINEER_LANES = Object.freeze(["engineer"]);

const MAX_READ_CHARS = 100000;

/** @returns {readonly ("engineer")[]} */
function engineerLaneIds() {
  return ENGINEER_LANES;
}

export class BasicTandemEngineerAIToolSet extends TandemAIToolSet {
  registerTools() {
    this.registerTool(TandemEngineerReadFileAITool);
    this.registerTool(TandemEngineerWriteFileAITool);
    this.registerTool(TandemEngineerListDirectoryAITool);
    this.registerTool(TandemEngineerCreateFolderAITool);
    this.registerTool(TandemEngineerMoveFileAITool);
    this.registerTool(TandemEngineerDuplicateFileAITool);
    this.registerTool(TandemEngineerEditFileAITool);
    this.registerTool(TandemEngineerGetProjectOverviewAITool);
    this.registerTool(TandemEngineerReadAgentEntryAITool);
    this.registerTool(TandemEngineerWriteAgentEntryAITool);
    this.registerTool(TandemEngineerReadAgentReadmeAITool);
    this.registerTool(TandemEngineerWriteAgentReadmeAITool);
  }

  detectFileType(path) {
    if (!path || typeof path !== "string") {
      return "unknown";
    }
    const extension = path.split(".").pop()?.toLowerCase();
    if (!extension) {
      return "text";
    }
    const binaryExtensions = {
      png: "image",
      jpg: "image",
      jpeg: "image",
      gif: "image",
      svg: "image",
      webp: "image",
      bmp: "image",
      ico: "image",
      tiff: "image",
      tif: "image",
      mp4: "video",
      webm: "video",
      avi: "video",
      mov: "video",
      mkv: "video",
      wmv: "video",
      flv: "video",
      m4v: "video",
      mp3: "audio",
      wav: "audio",
      ogg: "audio",
      m4a: "audio",
      aac: "audio",
      flac: "audio",
      wma: "audio",
      zip: "binary",
      rar: "binary",
      tar: "binary",
      gz: "binary",
      exe: "binary",
      dll: "binary",
      so: "binary",
      dylib: "binary",
      pdf: "binary",
      doc: "binary",
      docx: "binary",
      xls: "binary",
      xlsx: "binary",
    };
    return binaryExtensions[extension] || "text";
  }

  getMimeType(extension) {
    const mimeTypes = {
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      svg: "image/svg+xml",
      webp: "image/webp",
      bmp: "image/bmp",
      ico: "image/x-icon",
      tiff: "image/tiff",
      tif: "image/tiff",
      mp4: "video/mp4",
      webm: "video/webm",
      avi: "video/x-msvideo",
      mov: "video/quicktime",
      mkv: "video/x-matroska",
      wmv: "video/x-ms-wmv",
      flv: "video/x-flv",
      m4v: "video/mp4",
      mp3: "audio/mpeg",
      wav: "audio/wav",
      ogg: "audio/ogg",
      m4a: "audio/mp4",
      aac: "audio/aac",
      flac: "audio/flac",
      wma: "audio/x-ms-wma",
      pdf: "application/pdf",
      doc: "application/msword",
      docx:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      zip: "application/zip",
      rar: "application/x-rar-compressed",
      tar: "application/x-tar",
      gz: "application/gzip",
    };
    return mimeTypes[extension] || "application/octet-stream";
  }

  getFileTypeDescription(fileType, extension) {
    const descriptions = {
      image: `${extension.toUpperCase()} image file`,
      video: `${extension.toUpperCase()} video file`,
      audio: `${extension.toUpperCase()} audio file`,
      binary: `${extension.toUpperCase()} binary file`,
      unknown: "Unknown file type",
    };
    return descriptions[fileType] || descriptions.unknown;
  }

  async getFileMetadata(path, fileType) {
    try {
      const statsResult =
        await this.aiToolSystem.toolSystem.readFileTool.readFile(
          path,
          "binary"
        );

      if (!statsResult.success) {
        return {
          success: false,
          error: `Could not read file metadata: ${statsResult.message}`,
          path,
        };
      }

      const fileExtension = path.split(".").pop()?.toLowerCase() || "";
      const mimeType = this.getMimeType(fileExtension);

      return {
        success: true,
        data: {
          path,
          fileType,
          extension: fileExtension,
          size: statsResult.data.size,
          mimeType,
          description: this.getFileTypeDescription(fileType, fileExtension),
          note:
            "This is a binary file. Full content cannot be displayed as text.",
        },
        message: "File metadata retrieved successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to get file metadata: ${error.message}`,
        path,
      };
    }
  }

  /**
   * Line-based read (1-based offset/limit) over full-file UTF-8 read — no readFileWithLineNumbers on TandemTool.
   */
  async readFile({ path, offset = 1, limit } = {}) {
    if (!this.aiToolSystem?.toolSystem?.readFileTool) {
      throw new Error("ReadFileTool is not available");
    }

    const startLine = Math.max(1, offset);
    const fileType = this.detectFileType(path);

    if (fileType !== "text") {
      return this.getFileMetadata(path, fileType);
    }

    const result = await this.aiToolSystem.toolSystem.readFileTool.readFile(
      path,
      "utf8"
    );

    if (!result?.success) {
      return result;
    }

    const text = typeof result.data?.content === "string" ? result.data.content : "";
    const allLines = text.length === 0 ? [] : text.split(/\r?\n/);
    const totalLines = allLines.length;

    const startIdx = startLine - 1;
    const endIdxExclusive =
      limit != null
        ? Math.min(totalLines, startIdx + limit)
        : totalLines;
    const sliceLines = allLines.slice(startIdx, endIdxExclusive);
    const content = sliceLines.join("\n");

    if (content.length > MAX_READ_CHARS) {
      return {
        success: false,
        error: `File content (${content.length} characters) exceeds maximum allowed characters (${MAX_READ_CHARS}). Use offset and limit to read smaller ranges.`,
        contentLength: content.length,
        maxAllowed: MAX_READ_CHARS,
      };
    }

    const endLine =
      sliceLines.length > 0 ? startLine + sliceLines.length - 1 : startLine - 1;

    return {
      success: true,
      data: {
        path: result.data.path,
        content,
        startLine,
        endLine,
        totalLines,
      },
      message: "File read successfully",
    };
  }

  async writeFile({ path, content } = {}) {
    if (!this.aiToolSystem?.toolSystem?.writeFileTool) {
      throw new Error("WriteFileTool is not available");
    }
    return this.aiToolSystem.toolSystem.writeFileTool.writeFile(
      path,
      content,
      true,
      "utf8",
      true
    );
  }

  async listDirectory({ path } = {}) {
    if (!this.aiToolSystem?.toolSystem?.listDirectoryTool) {
      throw new Error("ListDirectoryTool is not available");
    }
    const includeStats = false;
    const includeHidden = false;
    const recursive = false;
    return this.aiToolSystem.toolSystem.listDirectoryTool.listDirectory(
      path,
      recursive,
      includeStats,
      includeHidden
    );
  }

  async createFolder({ path } = {}) {
    if (!this.aiToolSystem?.toolSystem?.createFolderTool) {
      throw new Error("CreateFolderTool is not available");
    }
    return this.aiToolSystem.toolSystem.createFolderTool.createFolder(
      path,
      true,
      true
    );
  }

  async moveFile({ sourcePath, destinationPath, overwrite = false } = {}) {
    if (!this.aiToolSystem?.toolSystem?.moveFileTool) {
      throw new Error("MoveFileTool is not available");
    }
    return this.aiToolSystem.toolSystem.moveFileTool.moveFile(
      sourcePath,
      destinationPath,
      overwrite
    );
  }

  async duplicateFile({ sourcePath, destinationPath, overwrite = false } = {}) {
    if (!this.aiToolSystem?.toolSystem?.duplicateFileTool) {
      throw new Error("DuplicateFileTool is not available");
    }
    return this.aiToolSystem.toolSystem.duplicateFileTool.duplicateFile(
      sourcePath,
      destinationPath,
      overwrite
    );
  }

  async editFile({ path, old_string, new_string, replace_all = false } = {}) {
    if (!this.aiToolSystem?.toolSystem?.editFileTool) {
      throw new Error("EditFileTool is not available");
    }
    return this.aiToolSystem.toolSystem.editFileTool.editFile(
      path,
      old_string,
      new_string,
      replace_all
    );
  }

  _tandemAppSummary() {
    const tm = this.aiToolSystem?.toolSystem?.tandemManager;
    const root = tm?.workingDirectoryPath;
    if (!root || typeof root !== "string" || !String(root).trim()) {
      return null;
    }
    const trimmed = String(root).trim().replace(/\\/g, "/");
    const segs = trimmed.split("/").filter(Boolean);
    const base = segs.length ? segs[segs.length - 1] : trimmed;
    return {
      name: base || null,
      description: null,
      appType: "tandem",
    };
  }

  async getProjectOverview({ latest_entry_count } = {}) {
    if (!this.aiToolSystem?.toolSystem?.listDirectoryTool) {
      throw new Error("ListDirectoryTool is not available");
    }

    const listTool = this.aiToolSystem.toolSystem.listDirectoryTool;

    try {
      const { tree: fileTree, budget: fileTreeBudget } =
        await buildProjectFileTreeForOverview(listTool);

      const appSummary = this._tandemAppSummary();

      const agentTool = this.aiToolSystem.toolSystem.agentEntriesTool;
      let agents = {
        agentsPresent: false,
        readmeBody: null,
        readmeNote: "AgentEntriesTool not available.",
        latestEntryCountRequested: 2,
        entryNumbersIncluded: [],
        entries: [],
      };
      if (agentTool?.collectAgentContextForOverview) {
        agents = await agentTool.collectAgentContextForOverview(
          latest_entry_count
        );
      }

      return {
        success: true,
        overview: {
          app: appSummary,
          fileTree,
          fileTreeBudget,
          agents,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  async readAgentEntry({ entry_number, through_entry_number } = {}) {
    const tool = this.aiToolSystem?.toolSystem?.agentEntriesTool;
    if (!tool) {
      throw new Error("AgentEntriesTool is not available");
    }
    if (
      through_entry_number === undefined ||
      through_entry_number === null
    ) {
      return tool.readEntry(entry_number);
    }
    return tool.readEntryRange(entry_number, through_entry_number);
  }

  async writeAgentEntry({ entry_number, content, readme_summary } = {}) {
    const agentTool = this.aiToolSystem?.toolSystem?.agentEntriesTool;
    if (!agentTool) {
      throw new Error("AgentEntriesTool is not available");
    }

    const n =
      typeof entry_number === "number"
        ? entry_number
        : parseInt(String(entry_number), 10);
    const padded =
      Number.isInteger(n) && n >= 1 && n <= 999
        ? String(n).padStart(3, "0")
        : null;

    const hasContent =
      content !== undefined && content !== null && typeof content === "string";
    const summaryOneLine =
      readme_summary == null || typeof readme_summary !== "string"
        ? ""
        : String(readme_summary).replace(/\s+/g, " ").trim();
    const hasSummary = summaryOneLine.length > 0;
    const readTool = this.aiToolSystem?.toolSystem?.readFileTool;
    const listTool = this.aiToolSystem?.toolSystem?.listDirectoryTool;

    if (!padded || (!hasContent && !hasSummary)) {
      return agentTool.writeEntry(entry_number, content, readme_summary);
    }

    if (readTool && listTool) {
      const agentsRootPath = "/agents";
      const readmePath = "/agents/README.md";
      const entryPath = `/agents/agent-entry-${padded}.md`;
      const agentsRootState = await captureProjectPathState({
        path: agentsRootPath,
        readTool,
        listTool,
      });
      const pathStates = {};

      if (!agentsRootState?.exists || agentsRootState?.kind === "missing") {
        pathStates[agentsRootPath] = agentsRootState;
      } else {
        if (hasSummary) {
          pathStates[readmePath] = await captureProjectPathState({
            path: readmePath,
            readTool,
            listTool,
          });
        }
        if (hasContent) {
          pathStates[entryPath] = await captureProjectPathState({
            path: entryPath,
            readTool,
            listTool,
          });
        }
      }

      const trackedPaths = Object.keys(pathStates);
      if (trackedPaths.length > 0) {
        const entryId = `write-agent-entry:${padded}:${Date.now()}`;
        this.createCheckpointEntry(entryId, {
          entryId,
          toolId: "write-agent-entry",
          type: "agent-write-paths",
          original: {
            pathStates,
          },
          meta: {
            entryNumber: n,
            rootPath: agentsRootPath,
            readmePath: hasSummary ? readmePath : null,
            entryPath: hasContent ? entryPath : null,
          },
        });
      }
    }

    return agentTool.writeEntry(entry_number, content, readme_summary);
  }

  async readAgentReadme() {
    const agentTool = this.aiToolSystem?.toolSystem?.agentEntriesTool;
    if (!agentTool) {
      throw new Error("AgentEntriesTool is not available");
    }
    return agentTool.readReadmeBody();
  }

  async writeAgentReadme({ body } = {}) {
    const agentTool = this.aiToolSystem?.toolSystem?.agentEntriesTool;
    if (!agentTool) {
      throw new Error("AgentEntriesTool is not available");
    }
    const readTool = this.aiToolSystem?.toolSystem?.readFileTool;
    const listTool = this.aiToolSystem?.toolSystem?.listDirectoryTool;
    if (readTool && listTool) {
      const agentsRootPath = "/agents";
      const readmePath = "/agents/README.md";
      const agentsRootState = await captureProjectPathState({
        path: agentsRootPath,
        readTool,
        listTool,
      });
      const pathStates = {};

      if (!agentsRootState?.exists || agentsRootState?.kind === "missing") {
        pathStates[agentsRootPath] = agentsRootState;
      } else {
        pathStates[readmePath] = await captureProjectPathState({
          path: readmePath,
          readTool,
          listTool,
        });
      }

      const trackedPaths = Object.keys(pathStates);
      if (trackedPaths.length > 0) {
        const entryId = `write-agent-readme:${Date.now()}`;
        this.createCheckpointEntry(entryId, {
          entryId,
          toolId: "write-agent-readme",
          type: "agent-write-paths",
          original: {
            pathStates,
          },
          meta: {
            rootPath: agentsRootPath,
            readmePath,
          },
        });
      }
    }

    return agentTool.writeReadmeBody(body);
  }
}

class TandemEngineerReadFileAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "read-file";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "read-file",
        description:
          "Read file contents from the Tandem project folder. `offset` and `limit` are line-based (1-based line numbers). For large files, use smaller ranges.",
        parameters: {
          type: "object",
          properties: {
            path: { type: "string", description: "Path to the file (required)" },
            offset: {
              type: "integer",
              description: "1-based starting line (default 1)",
            },
            limit: {
              type: "integer",
              description: "Max lines from offset onward; omit for full file",
            },
          },
          required: ["path"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ path, offset, limit } = {}) {
    return this.aiToolSet.readFile({ path, offset, limit });
  }
}

class TandemEngineerWriteFileAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "write-file";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "write-file",
        description:
          "Create or overwrite a file under the Tandem project folder. Parent directories are created automatically.",
        parameters: {
          type: "object",
          properties: {
            path: { type: "string", description: "File path (required)" },
            content: { type: "string", description: "Full file content (required)" },
          },
          required: ["path", "content"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ path, content } = {}) {
    const truncation = this.getCurrentToolCallTruncation();

    if (!truncation) {
      return this.aiToolSet.writeFile({ path, content });
    }

    const hasPath = typeof path === "string" && path.trim().length > 0;
    const hasContent = typeof content === "string";
    const summary =
      typeof truncation.summary === "string" && truncation.summary.trim()
        ? truncation.summary.trim()
        : "This turn hit its output token budget before the tool call finished streaming.";
    const maxOutputTokens =
      truncation.maxOutputTokens != null ? truncation.maxOutputTokens : null;

    if (!hasPath) {
      return {
        success: false,
        error: `${summary} No usable file path was in the truncated arguments, so nothing was written.`,
        truncation: {
          wasTruncated: true,
          summary,
          maxOutputTokens,
        },
      };
    }

    const effectiveContent = hasContent ? content : "";
    const writeResult = await this.aiToolSet.writeFile({
      path,
      content: effectiveContent,
    });

    const continuation =
      "The partial content that arrived was written. Continue with edit-file for the missing tail if needed.";

    return {
      ...writeResult,
      truncation: {
        wasTruncated: true,
        summary: `${summary} ${continuation}`,
        maxOutputTokens,
        bytesWritten: effectiveContent.length,
      },
    };
  }
}

class TandemEngineerListDirectoryAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "list-directory";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "list-directory",
        description:
          "List files and subdirectories at a path under the Tandem project folder.",
        parameters: {
          type: "object",
          properties: {
            path: { type: "string", description: "Directory path (required)" },
          },
          required: ["path"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ path } = {}) {
    return this.aiToolSet.listDirectory({ path });
  }
}

class TandemEngineerCreateFolderAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "create-folder";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "create-folder",
        description:
          "Create directory(ies). Paths are relative to the Tandem project root; parents are created as needed.",
        parameters: {
          type: "object",
          properties: {
            path: {
              oneOf: [
                { type: "string" },
                { type: "array", items: { type: "string" } },
              ],
              description: "Single path or array of paths (required)",
            },
          },
          required: ["path"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ path } = {}) {
    return this.aiToolSet.createFolder({ path });
  }
}

class TandemEngineerMoveFileAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "move-file";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "move-file",
        description: "Move or rename a file or directory within the project folder.",
        parameters: {
          type: "object",
          properties: {
            sourcePath: { type: "string", description: "Source path (required)" },
            destinationPath: {
              type: "string",
              description: "Destination path (required)",
            },
            overwrite: {
              type: "boolean",
              description: "Replace destination if it exists (default false)",
            },
          },
          required: ["sourcePath", "destinationPath"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ sourcePath, destinationPath, overwrite = false } = {}) {
    return this.aiToolSet.moveFile({
      sourcePath,
      destinationPath,
      overwrite,
    });
  }
}

class TandemEngineerDuplicateFileAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "duplicate-file";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "duplicate-file",
        description: "Copy a file or directory to a new path within the project folder.",
        parameters: {
          type: "object",
          properties: {
            sourcePath: { type: "string", description: "Source path (required)" },
            destinationPath: {
              type: "string",
              description: "Destination path (required)",
            },
            overwrite: {
              type: "boolean",
              description: "Replace destination if it exists (default false)",
            },
          },
          required: ["sourcePath", "destinationPath"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ sourcePath, destinationPath, overwrite = false } = {}) {
    return this.aiToolSet.duplicateFile({
      sourcePath,
      destinationPath,
      overwrite,
    });
  }
}

class TandemEngineerEditFileAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "edit-file";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "edit-file",
        description:
          "Replace text in a file: first occurrence of old_string with new_string unless replace_all is true.",
        parameters: {
          type: "object",
          properties: {
            path: { type: "string", description: "File path (required)" },
            old_string: {
              type: "string",
              description: "Exact text to find (required, non-empty)",
            },
            new_string: { type: "string", description: "Replacement (required)" },
            replace_all: {
              type: "boolean",
              description: "Replace all occurrences (default false)",
            },
          },
          required: ["path", "old_string", "new_string"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ path, old_string, new_string, replace_all = false } = {}) {
    const truncation = this.getCurrentToolCallTruncation();

    if (truncation) {
      const summary =
        typeof truncation.summary === "string" && truncation.summary.trim()
          ? truncation.summary.trim()
          : "This turn hit its output token budget before the tool call finished streaming.";
      const maxOutputTokens =
        truncation.maxOutputTokens != null ? truncation.maxOutputTokens : null;

      return {
        success: false,
        error: `${summary} edit-file arguments were cut off mid-stream; nothing was changed. Split old_string/new_string into smaller chunks and retry.`,
        truncation: {
          wasTruncated: true,
          summary,
          maxOutputTokens,
        },
      };
    }

    return this.aiToolSet.editFile({
      path,
      old_string,
      new_string,
      replace_all,
    });
  }
}

class TandemEngineerGetProjectOverviewAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "get-project-overview";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "get-project-overview",
        description:
          "One-shot project orientation for the Tandem folder: project title (folder name), file tree up to 3 levels from root (max 20 items per folder; overflow summarized), and agent memory (README body + latest N agent-entry files, oldest→newest). Does not create /agents. Optional latest_entry_count (default 2, max 50, 0 = skip entry file contents).",
        parameters: {
          type: "object",
          properties: {
            latest_entry_count: {
              type: "integer",
              description:
                "Optional. How many most recent agent-entry-NNN.md files to include. Default 2. Use 0 for README-only agent section. Max 50.",
            },
          },
          required: [],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ latest_entry_count } = {}) {
    return this.aiToolSet.getProjectOverview({ latest_entry_count });
  }
}

class TandemEngineerReadAgentEntryAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "read-agent-entry";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "read-agent-entry",
        description:
          "Read project agent memory entries from the Tandem project /agents folder by entry number (no file paths). Use entry_number for a single file, or entry_number plus through_entry_number for an inclusive range (max 100 entries per call). Files are /agents/agent-entry-NNN.md (three-digit numbers).",
        parameters: {
          type: "object",
          properties: {
            entry_number: {
              type: "integer",
              description:
                "Agent entry number 1–999 (required). Maps to agent-entry-NNN.md.",
            },
            through_entry_number: {
              type: "integer",
              description:
                "Optional end of inclusive range (also 1–999). If omitted, only entry_number is read.",
            },
          },
          required: ["entry_number"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ entry_number, through_entry_number } = {}) {
    return this.aiToolSet.readAgentEntry({
      entry_number,
      through_entry_number,
    });
  }
}

class TandemEngineerWriteAgentEntryAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "write-agent-entry";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "write-agent-entry",
        description:
          "Create or update a numbered agent entry under /agents in the Tandem project. Use sparingly: not after every small change, a single short prompt, or only because many tool calls ran—wait for the user to verify or clearly ask to record. Prefer when the user explicitly requests it, or when they signal satisfaction or moving on after verification. Do not write an entry as the first action of a turn unless the user’s message is about agent memory. Provide content (full markdown for agent-entry-NNN.md) and/or readme_summary (one-line index blurb): at least one is required. Omit content to only change the README index line for this number; omit readme_summary to only write the entry file. README is auto-created with a default body and index section when needed.",
        parameters: {
          type: "object",
          properties: {
            entry_number: {
              type: "integer",
              description: "Agent entry number 1–999 (required).",
            },
            content: {
              type: "string",
              description:
                "Optional. Full markdown for agent-entry-NNN.md. Omit to only update the index line (with readme_summary).",
            },
            readme_summary: {
              type: "string",
              description:
                "Optional. Summary for the Agent Entry Index (collapsed to one line). Omit to only write the entry file (content).",
            },
          },
          required: ["entry_number"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ entry_number, content, readme_summary } = {}) {
    return this.aiToolSet.writeAgentEntry({
      entry_number,
      content,
      readme_summary,
    });
  }

  async restoreEntry(entry) {
    const pathStates = entry?.original?.pathStates || {};
    const trackedStates = Object.values(pathStates).filter(Boolean);
    if (trackedStates.length === 0) {
      return { success: true };
    }

    const readTool = this.toolSystem?.readFileTool;
    const listTool = this.toolSystem?.listDirectoryTool;
    const deleteTool = this.toolSystem?.deleteFileTool;
    const writeTool = this.toolSystem?.writeFileTool;
    const createFolderTool = this.toolSystem?.createFolderTool;
    if (!readTool || !listTool || !deleteTool || !writeTool || !createFolderTool) {
      throw new Error("Agent write tool dependencies are not available for restore");
    }

    const redoPathStates = {};
    for (const state of trackedStates) {
      redoPathStates[state.path] = await captureProjectPathState({
        path: state.path,
        readTool,
        listTool,
      });
    }

    this.createRedoCheckpointEntry(entry?.entryId, {
      entryId: entry?.entryId,
      toolId: entry?.toolId,
      type: entry?.type,
      original: {
        pathStates: redoPathStates,
      },
      meta: entry?.meta || {},
    });

    for (const state of trackedStates) {
      const restoreResult = await restoreProjectPathState(state, {
        readTool,
        deleteTool,
        writeTool,
        createFolderTool,
      });
      if (!restoreResult?.success) {
        return restoreResult;
      }
    }

    return { success: true };
  }

  async restoreRedoEntry(entry) {
    const pathStates = entry?.original?.pathStates || {};
    const trackedStates = Object.values(pathStates).filter(Boolean);
    if (trackedStates.length === 0) {
      return { success: true };
    }

    const readTool = this.toolSystem?.readFileTool;
    const deleteTool = this.toolSystem?.deleteFileTool;
    const writeTool = this.toolSystem?.writeFileTool;
    const createFolderTool = this.toolSystem?.createFolderTool;
    if (!readTool || !deleteTool || !writeTool || !createFolderTool) {
      throw new Error("Agent write tool dependencies are not available for redo restore");
    }

    for (const state of trackedStates) {
      const restoreResult = await restoreProjectPathState(state, {
        readTool,
        deleteTool,
        writeTool,
        createFolderTool,
      });
      if (!restoreResult?.success) {
        return restoreResult;
      }
    }

    return { success: true };
  }
}

class TandemEngineerReadAgentReadmeAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "read-agent-readme";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "read-agent-readme",
        description:
          "Read the editable instructions section of agents/README.md (the block above the Agent Entry Index). Does not return the machine-managed index list. Creates a default README under /agents if missing. Prefer this over read-file on /agents/README.md for policy text.",
        parameters: {
          type: "object",
          properties: {},
          required: [],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute() {
    return this.aiToolSet.readAgentReadme();
  }
}

class TandemEngineerWriteAgentReadmeAITool extends BaseAITool {
  constructor() {
    super();
    this.toolId = "write-agent-readme";
    this.bubbleStyle = "simple";
    this.definition = {
      type: "function",
      function: {
        name: "write-agent-readme",
        description:
          "Replace the editable instructions section of agents/README.md (between HTML comment markers). Preserves the Agent Entry Index and content below it. Creates default /agents/README.md if missing. Do not use this to edit index lines; use write-agent-entry with readme_summary for those.",
        parameters: {
          type: "object",
          properties: {
            body: {
              type: "string",
              description:
                "Full markdown for the instructions body only (required).",
            },
          },
          required: ["body"],
        },
      },
    };
  }

  get tandemLaneIds() {
    return engineerLaneIds();
  }

  async execute({ body } = {}) {
    return this.aiToolSet.writeAgentReadme({ body });
  }

  async restoreEntry(entry) {
    const pathStates = entry?.original?.pathStates || {};
    const trackedStates = Object.values(pathStates).filter(Boolean);
    if (trackedStates.length === 0) {
      return { success: true };
    }

    const readTool = this.toolSystem?.readFileTool;
    const listTool = this.toolSystem?.listDirectoryTool;
    const deleteTool = this.toolSystem?.deleteFileTool;
    const writeTool = this.toolSystem?.writeFileTool;
    const createFolderTool = this.toolSystem?.createFolderTool;
    if (!readTool || !listTool || !deleteTool || !writeTool || !createFolderTool) {
      throw new Error("Agent write tool dependencies are not available for restore");
    }

    const redoPathStates = {};
    for (const state of trackedStates) {
      redoPathStates[state.path] = await captureProjectPathState({
        path: state.path,
        readTool,
        listTool,
      });
    }

    this.createRedoCheckpointEntry(entry?.entryId, {
      entryId: entry?.entryId,
      toolId: entry?.toolId,
      type: entry?.type,
      original: {
        pathStates: redoPathStates,
      },
      meta: entry?.meta || {},
    });

    for (const state of trackedStates) {
      const restoreResult = await restoreProjectPathState(state, {
        readTool,
        deleteTool,
        writeTool,
        createFolderTool,
      });
      if (!restoreResult?.success) {
        return restoreResult;
      }
    }

    return { success: true };
  }

  async restoreRedoEntry(entry) {
    const pathStates = entry?.original?.pathStates || {};
    const trackedStates = Object.values(pathStates).filter(Boolean);
    if (trackedStates.length === 0) {
      return { success: true };
    }

    const readTool = this.toolSystem?.readFileTool;
    const deleteTool = this.toolSystem?.deleteFileTool;
    const writeTool = this.toolSystem?.writeFileTool;
    const createFolderTool = this.toolSystem?.createFolderTool;
    if (!readTool || !deleteTool || !writeTool || !createFolderTool) {
      throw new Error("Agent write tool dependencies are not available for redo restore");
    }

    for (const state of trackedStates) {
      const restoreResult = await restoreProjectPathState(state, {
        readTool,
        deleteTool,
        writeTool,
        createFolderTool,
      });
      if (!restoreResult?.success) {
        return restoreResult;
      }
    }

    return { success: true };
  }
}
